import React, { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  dialCodeCountries,
  composePhoneNumber,
  splitPhoneNumber,
  countryFlagEmoji,
  GREECE,
} from '../../data/countries';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
`;

const FieldLabel = styled.label<{ $error?: boolean }>`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => (props.$error ? props.theme.colors.error : props.theme.colors.text)};
  font-family: ${typography.fontFamily.primary};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(9.5rem, 12rem) 1fr;
  gap: ${spacing[2]};
  width: 100%;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const controlStyles = `
  padding: ${spacing[3]} ${spacing[4]};
  border-width: ${borders.width.base};
  border-style: solid;
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  width: 100%;
`;

const DialSelect = styled.select<{ $error?: boolean }>`
  ${controlStyles}
  border-color: ${props => (props.$error ? props.theme.colors.error : props.theme.colors.border)};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  &:focus {
    outline: none;
    border-color: ${props => (props.$error ? props.theme.colors.error : props.theme.colors.primary)};
    box-shadow: ${props =>
      shadows.colored.sm(props.$error ? props.theme.colors.error : props.theme.colors.primary)};
  }
`;

const NumberInput = styled.input<{ $error?: boolean }>`
  ${controlStyles}
  border-color: ${props => (props.$error ? props.theme.colors.error : props.theme.colors.border)};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  &:focus {
    outline: none;
    border-color: ${props => (props.$error ? props.theme.colors.error : props.theme.colors.primary)};
    box-shadow: ${props =>
      shadows.colored.sm(props.$error ? props.theme.colors.error : props.theme.colors.primary)};
  }
  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;

const ErrorText = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

export interface PhoneWithDialCodeProps {
  id?: string;
  name?: string;
  label: string;
  value: string;
  onChange: (fullPhone: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  autoComplete?: string;
  /** Max digits for the national number part (default 15). */
  maxLength?: number;
}

export const PhoneWithDialCode: React.FC<PhoneWithDialCodeProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  required = false,
  autoComplete = 'tel',
  maxLength = 15,
}) => {
  const { t, i18n } = useTranslation();
  const reactId = useId();
  const fieldId = id || `phone-dial-${reactId}`;
  const dialId = `${fieldId}-dial`;
  const numberId = `${fieldId}-number`;
  const errorId = error ? `${fieldId}-error` : undefined;
  const isGreek =
    (i18n.language || 'en').startsWith('gr') || (i18n.language || 'en').startsWith('el');

  const { dialCode, nationalNumber } = useMemo(() => splitPhoneNumber(value), [value]);

  const dialOptions = useMemo(() => {
    // Unique dial codes: prefer first country for shared codes (+1 US before CA already in list)
    const seen = new Set<string>();
    return dialCodeCountries.filter(c => {
      if (seen.has(c.dialCode)) return false;
      seen.add(c.dialCode);
      return true;
    });
  }, []);

  const emit = (nextDial: string, nextNational: string) => {
    onChange(composePhoneNumber(nextDial, nextNational));
  };

  return (
    <Wrapper>
      <FieldLabel as="label" htmlFor={numberId} $error={!!error}>
        {label}
        {required && <span style={{ color: 'var(--color-error, #dc3545)', marginLeft: 2 }}>*</span>}
      </FieldLabel>
      <Row>
        <DialSelect
          id={dialId}
          aria-label={t('register.phoneCountryCode', { defaultValue: 'Country code' })}
          value={dialCode || GREECE.dialCode}
          onChange={e => emit(e.target.value, nationalNumber)}
          onBlur={onBlur}
          $error={!!error}
          aria-invalid={!!error}
          aria-describedby={errorId}
        >
          {dialOptions.map(c => (
            <option key={c.code} value={c.dialCode}>
              {countryFlagEmoji(c.code)} {c.dialCode} {isGreek ? c.nameGr : c.nameEn}
            </option>
          ))}
        </DialSelect>
        <NumberInput
          id={numberId}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete={autoComplete}
          value={nationalNumber}
          maxLength={maxLength}
          placeholder={t('register.phoneNumberPlaceholder', { defaultValue: 'Phone number' })}
          onChange={e => emit(dialCode || GREECE.dialCode, e.target.value)}
          onBlur={onBlur}
          $error={!!error}
          aria-invalid={!!error}
          aria-required={required || undefined}
          aria-describedby={errorId}
        />
      </Row>
      {error && (
        <ErrorText id={errorId} role="alert">
          {error}
        </ErrorText>
      )}
    </Wrapper>
  );
};

export default PhoneWithDialCode;
